/**
 * Org-switcher context resolver (GH #2293).
 *
 * Pure, side-effect-free derivation of `{ currentOrg, organizations }` for the
 * admin-portal OrgSwitcher, extracted from PortalLayout so the degradation
 * behavior is unit-testable without mounting the whole layout (router + query
 * client + auth store).
 *
 * The #2293 UI defect: while IMPERSONATING, if `GET /api/admin/tenants/accessible`
 * 401s (or the impersonated tenant is simply absent from the operator's
 * accessible set), the prior logic fell through to a '(unknown tenant)' label
 * AND collapsed `organizations` to `[currentOrg]` (length 1), so the switcher --
 * gated on `organizations.length > 1` -- vanished and the operator lost the
 * "exit impersonation" affordance entirely. The impersonated target tenant is
 * not guaranteed to be in the operator's accessible-tenants set, so the
 * accessible list can never be the sole source of the switcher's contents while
 * impersonating.
 *
 * The fix: while impersonating, `organizations` ALWAYS contains the apex "exit"
 * row plus the current (impersonated) org, independent of the accessible-tenants
 * fetch, so the switcher renders and exit-to-apex stays reachable; and
 * `currentOrg` is seeded from the impersonation target with a legible label
 * rather than '(unknown tenant)'.
 */

import type { Organization } from '@aaimplatform/ui';
import { hasPlatformAdminRole } from '../lib/platform-admin-roles';
import type { components } from '@aaimplatform/institutional-types/platform-admin';

/**
 * The generated tenant row (GET /api/admin/tenants/accessible returns
 * TenantListResponse.data: Tenant[]). OpenAPI-first: no manual interface.
 */
export type AccessibleTenant = components['schemas']['Tenant'];

/**
 * The subset of the generated PlatformUser this resolver reads. A Pick of the
 * canonical shape, not a hand-authored interface -- so a spec change to either
 * field is a compile error here rather than silent drift.
 */
export type OrgContextUser = Pick<components['schemas']['PlatformUser'], 'tenantId' | 'impersonatedBy'> &
  Partial<Pick<components['schemas']['PlatformUser'], 'roles'>>;

/** Apex tenant id ("Aaim, Inc."). Mirrors APEX_TENANT_ID from
 * @aaimplatform/database/security/tenant-resolver per
 * .claude/rules/multi-tenant-request-shape.md. */
export const APEX_TENANT_ID = '00000000-0000-0000-0000-000000aaaaaa';
export const APEX_TENANT_NAME = 'Aaim, Inc.';

/** The apex "Aaim, Inc." self / exit-impersonation row. */
export const APEX_ORG: Organization = {
  id: APEX_TENANT_ID,
  name: APEX_TENANT_NAME,
  type: 'Platform Administration',
};

export interface OrgContext {
  currentOrg: Organization;
  organizations: Organization[];
  /**
   * True only in the genuinely-unresolvable NON-impersonation case (an FI
   * tenantId that is neither apex nor in the accessible list). The layout logs
   * a drift warning when this is set. Impersonation never sets this -- it seeds
   * a legible label instead.
   */
  unresolved: boolean;
}

/**
 * The switcher's second line for a tenant row: its institution type and its
 * purpose (GH #4096; operator ruling 2026-10-22, "shown in the admin and
 * platform portals"), so a DEMO or PROBE tenant is told apart from a customer
 * before the operator enters it. Both are generated enum codes; the kit
 * OrgSwitcher and OrgSwitcherDialog print the line with underscores as spaces,
 * and their search reads it, so typing "demo" finds the demo tenants.
 */
export function tenantSubLabel(t: Pick<AccessibleTenant, 'type' | 'purpose'>): string {
  return `${t.type} | ${t.purpose}`;
}

function seedImpersonatedName(activeTenantId: string): string {
  // No tenant name is carried on the impersonation JWT (PlatformUser has no
  // tenantName field). Label it legibly and identifiably (short tenant id)
  // rather than the prior '(unknown tenant)'.
  return `Tenant ${activeTenantId.slice(0, 8)}`;
}

/**
 * Resolve the OrgSwitcher's `{ currentOrg, organizations }` from the current
 * user and the (possibly failed / empty) accessible-tenants list.
 *
 * Non-impersonation behavior is preserved exactly: apex admins see the full
 * accessible list; a single-tenant FI operator gets `[currentOrg]` (length 1 ->
 * switcher self-hides). Only the impersonation path is hardened.
 */
export function resolveOrgContext(
  user: OrgContextUser | null | undefined,
  accessibleTenants: readonly AccessibleTenant[] | undefined,
): OrgContext {
  const activeTenantId = user?.tenantId ?? null;
  const isImpersonating = !!user?.impersonatedBy;
  const list: Organization[] = (accessibleTenants ?? []).map((t) => ({
    id: t.id,
    name: t.name,
    type: tenantSubLabel(t),
  }));

  // --- currentOrg ---------------------------------------------------------
  let currentOrg: Organization;
  let unresolved = false;

  if (
    activeTenantId === APEX_TENANT_ID ||
    !activeTenantId ||
    // GH #3253 follow-up: a platform admin who is not impersonating acts as
    // apex on the server whatever its tenantId claim says
    // (resolveEffectiveTenantId step 2). e2etesting@aaim.com carries the E2E
    // test tenant, which /tenants/accessible does not list, so this path read
    // '(unknown tenant)'.
    (!isImpersonating && hasPlatformAdminRole(user?.roles))
  ) {
    // Apex context, or a legacy platform-admin path before impersonation.
    currentOrg = APEX_ORG;
  } else {
    const match = list.find((t) => t.id === activeTenantId);
    if (match) {
      currentOrg = match;
    } else if (isImpersonating) {
      // #2293: impersonated target absent from the accessible set (401/stale).
      // Seed a legible label from the impersonation target instead of the prior
      // '(unknown tenant)'.
      currentOrg = {
        id: activeTenantId,
        name: seedImpersonatedName(activeTenantId),
        type: 'Impersonated tenant',
      };
    } else {
      // Genuinely unresolvable, NON-impersonation: keep the opaque label + the
      // drift warning (the layout logs when `unresolved`).
      currentOrg = { id: activeTenantId, name: '(unknown tenant)', type: 'Tenant' };
      unresolved = true;
    }
  }

  // --- organizations ------------------------------------------------------
  let organizations: Organization[];

  if (isImpersonating) {
    // #2293: while impersonating, the switcher list ALWAYS carries the apex
    // exit row + the current org, independent of the accessible-tenants fetch,
    // so the switcher renders (length >= 2) and exit-to-apex stays reachable
    // even if accessible-tenants 401'd or omitted the impersonated tenant.
    const byId = new Map<string, Organization>();
    byId.set(APEX_ORG.id, APEX_ORG);
    for (const o of list) byId.set(o.id, o);
    byId.set(currentOrg.id, currentOrg);
    organizations = [...byId.values()];
  } else if (list.length === 0) {
    // Accessible list loading or degenerate-empty: show only the resolved
    // current org so the trigger is never empty (single-tenant FI operator ->
    // length 1 -> switcher self-hides, preserved).
    organizations = [currentOrg];
  } else {
    organizations = list;
  }

  return { currentOrg, organizations, unresolved };
}
